import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ChevronRight } from "lucide-react";
import { JobPosting } from "@/services/api/types";
import { useToast } from "@/app/hooks/use-toast";
import { ApplicationFormSkeleton } from "@/components/jobs/ApplicationFormSkeleton";
import DcPage from "@/components/landing/dcv2/DcPage";
import { useAuth } from "@/app/hooks/useAuth";
import { jobsApi } from "@/services/endpoints/jobs";
import { resumeApi } from "@/services/endpoints/resume";
import { glassCardStyle, surfaceStyle } from "@/components/auth/authStyles";
import { normalizeMutationDateValue } from "@/utils/timezone";
import { formatDate } from "@/utils/formatters/date.formatters";
import {
  APPLICATION_STEPS as steps,
  ApplicationStepper,
  CompletionRing,
  JobContextPanel,
  ResumeStep,
  PersonalInfoStep,
  SummaryStep,
  ExperienceStep,
  EducationStep,
  SkillsStep,
  ReviewStep,
  APPLICATION_LIMITS,
  buildExpectedSalary,
  hasIssues,
  issueMessages,
  validateCertificationDraft,
  validateEducationEntry,
  validateExperienceEntry,
  validatePersonalInfo,
  validateResumeUrl,
  validateSkills,
  validateSummary,
  visibleErrors,
} from "@/components/jobs/application";
import type {
  WorkExperience,
  EducationEntry,
  CertificationEntry,
  ApplicationFormData,
} from "@/components/jobs/application";

const MAX_ENTRIES = APPLICATION_LIMITS.MAX_ENTRIES;
/** Steps whose fields are sent with the application (the resume upload and Review are not). */
const SUBMITTED_STEPS = [2, 3, 4, 5, 6] as const;

/** Certifications missing a name, issuer or either date are not sent. */
const isSentCertification = (cert: CertificationEntry) =>
  !!((cert.name || '').trim() && (cert.issuer || '').trim() && cert.issueDate && cert.expiryDate);

const normalizeYearMonth = (value?: string | null) => {
  if (!value) return '';
  const trimmed = value.trim();
  if (!trimmed) return '';
  if (/^\d{4}-\d{2}$/.test(trimmed)) return trimmed;
  const isoMatch = trimmed.match(/^(\d{4}-\d{2})/);
  if (isoMatch) return isoMatch[1];
  const slashMatch = trimmed.match(/^(\d{4})[\/](\d{1,2})/);
  if (slashMatch) {
    const [, year, month] = slashMatch;
    return `${year}-${month.padStart(2, '0')}`;
  }
  return trimmed;
};

const extractYear = (value?: string | number | null) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'number') {
    return value > 0 ? String(value) : '';
  }
  const trimmed = value.trim();
  if (!trimmed) return '';
  const yearMatch = trimmed.match(/(\d{4})/);
  return yearMatch ? yearMatch[1] : '';
};

const deriveFieldOfStudyFromDegree = (degree?: string | null) => {
  if (!degree) return '';
  const normalized = degree.toLowerCase();
  
  // Handle "MASTER OF BUSINESS ADMINISTRATION" pattern - extract after "OF"
  const ofMatch = normalized.match(/\b(master|bachelor|doctor|associate)\s+of\s+(.+)/i);
  if (ofMatch) {
    const fieldPart = ofMatch[2].trim();
    // Remove common suffixes
    const cleaned = fieldPart
      .replace(/\b(degree|administration|studies|program)\b/gi, '')
      .trim();
    if (cleaned) {
      // Title case the result
      return cleaned.split(/\s+/).map(word => 
        word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      ).join(' ');
    }
  }

  // Handle "in" pattern - e.g., "bachelor of science in Information technology"
  const inMatch = normalized.match(/\bin\s+(.+)/i);
  if (inMatch) {
    const fieldPart = degree.substring(degree.toLowerCase().indexOf(' in ') + 4).trim();
    // Remove common prefixes/suffixes
    const cleaned = fieldPart
      .replace(/^[^a-zA-Z]*/, '')
      .replace(/[^a-zA-Z\s]*$/, '')
      .replace(/\b(bachelor|master|associate|doctor|of|science|arts|degree|administration|studies|program)\b/gi, '')
      .trim();
    if (cleaned) {
      // Preserve original capitalization if it looks intentional (has mixed case)
      if (cleaned !== cleaned.toLowerCase() && cleaned !== cleaned.toUpperCase()) {
        return cleaned;
      }
      // Otherwise title case
      return cleaned.split(/\s+/).map(word => 
        word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      ).join(' ');
    }
  }

  // Handle certification patterns - e.g., "PRODUCT MANAGEMENT CERTIFICATION"
  const certMatch = normalized.match(/(.+?)\s+certification/i);
  if (certMatch) {
    const fieldPart = certMatch[1].trim();
    // Title case
    return fieldPart.split(/\s+/).map(word => 
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join(' ');
  }

  // Standard mapping for common fields
  const mapping = [
    { regex: /(account|accountancy)/i, field: "Accounting" },
    { regex: /finance/i, field: "Finance" },
    { regex: /computer|software|cs|informatics/i, field: "Computer Science" },
    { regex: /information technology|it/i, field: "Information Technology" },
    { regex: /engineering/i, field: "Engineering" },
    { regex: /marketing/i, field: "Marketing" },
    { regex: /business\s+administration/i, field: "Business Administration" },
    { regex: /business/i, field: "Business Administration" },
    { regex: /economics/i, field: "Economics" },
    { regex: /psychology/i, field: "Psychology" },
    { regex: /mathematics|math/i, field: "Mathematics" },
    { regex: /product\s+management/i, field: "Product Management" },
    { regex: /management/i, field: "Management" }
  ];

  for (const entry of mapping) {
    if (entry.regex.test(degree)) {
      return entry.field;
    }
  }

  return '';
};

export default function JobApplication() {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { getAccessToken, user, isAuthenticated } = useAuth();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [job, setJob] = useState<JobPosting | null>(null);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: user?.email || "",
    phone: "",
    phoneCountryCode: "+1-US",
    headline: "",
    professionalSummary: "",
    resumeUrl: "",
    totalExperienceYears: "",
    expectedSalary: "",
    salaryCurrency: "USD",
    country: "United States"
  });
  
  const [originalSalary, setOriginalSalary] = useState<number | null>(null);
  const [originalCurrency, setOriginalCurrency] = useState<string>("USD");
  const [converting, setConverting] = useState(false);
  
  const [workExperience, setWorkExperience] = useState<WorkExperience[]>([]);
  const [educationEntries, setEducationEntries] = useState<EducationEntry[]>([]);
  const [certificationEntries, setCertificationEntries] = useState<CertificationEntry[]>([]);
  const [skills, setSkills] = useState<string[]>([]);
  const [newCertification, setNewCertification] = useState({
    name: "",
    issuer: "",
    issueDate: "",
    expiryDate: "",
    credentialId: "",
    awardedTo: ""
  });
  const [certificationUrlError, setCertificationUrlError] = useState("");
  // Steps the candidate tried to leave: their "required" / "too short"
  // messages are revealed. Over-limit and format errors always show live.
  const [revealedSteps, setRevealedSteps] = useState<Record<number, boolean>>({});
  const [certDraftAttempted, setCertDraftAttempted] = useState(false);
  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [parsingResume, setParsingResume] = useState(false);
  const [resumeParsed, setResumeParsed] = useState(false);

  useEffect(() => {
    if (jobId) {
      fetchJobDetails();
    }
    
    // Pre-fill email from AuthContext (firstName and lastName should come from resume)
    if (user) {
      setFormData(prev => ({
        ...prev,
        email: user.email || prev.email
      }));
    }
  }, [jobId, user]);

  const fetchJobDetails = async () => {
    try {
      // Authenticated fetch (jobsApi.getById attaches the candidate token) so the
      // response carries candidateApplicationContext.hasApplied.
      const data = await jobsApi.getById(jobId!);

      // Block the wizard up front if this candidate already applied — the BE
      // also rejects a duplicate apply with 409, this just avoids wasted effort.
      if (data.data.candidateApplicationContext?.hasApplied) {
        toast({
          title: "Already applied",
          description: "You've already applied to this position. You can't apply twice.",
        });
        navigate(`/job/${jobId}`);
        return;
      }

      setJob(data.data);
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to load job details.",
        variant: "destructive"
      });
      navigate('/jobs');
    }
  };

  const handleResumeUpload = async (file: File) => {
    // Check authentication first
    if (!isAuthenticated) {
      toast({
        title: "Authentication Required",
        description: "Please log in to upload and parse your resume.",
        variant: "destructive"
      });
      navigate(`/auth?redirect=${encodeURIComponent(`/apply/${jobId}`)}`);
      return;
    }

    const accessToken = getAccessToken();
    if (!accessToken) {
      toast({
        title: "Authentication Required",
        description: "Please log in to upload and parse your resume.",
        variant: "destructive"
      });
      navigate(`/auth?redirect=${encodeURIComponent(`/apply/${jobId}`)}`);
      return;
    }

    setParsingResume(true);
    setResumeFile(file);
    
    try {
      const result = await resumeApi.parseResume(file, accessToken);
      
      if (result.success && result.data) {
        const parsedData = result.data;
        
        // Autofill form data from parsed resume
        // First name and last name should come from resume, not account
        if (parsedData.firstName) {
          setFormData(prev => ({ ...prev, firstName: parsedData.firstName || prev.firstName }));
        }
        if (parsedData.lastName) {
          setFormData(prev => ({ ...prev, lastName: parsedData.lastName || prev.lastName }));
        }
        if (parsedData.email && !formData.email && !user?.email) {
          setFormData(prev => ({ ...prev, email: parsedData.email || prev.email }));
        }
        if (parsedData.phone && !formData.phone) {
          // Extract country code if present in phone
          const phoneValue = parsedData.phone.trim();
          if (phoneValue.startsWith('+')) {
            // Try to match country code from the dropdown options
            // Common country codes: +1, +44, +61, +49, +33, +81, +91, +86, +55, +52, +63, +65, +82, +31, +46, +41, +34, +39, +7, +27, +971, +966
            const countryCodePatterns = [
              '+971', '+966', '+63', '+65', '+82', '+86', '+91', '+55', '+52', '+44', '+61', '+49', '+33', '+81', '+31', '+46', '+41', '+34', '+39', '+7', '+27', '+1'
            ];
            
            let matchedCode = '+1'; // Default
            let phoneNumber = phoneValue;
            
            // Find matching country code (try longer codes first to avoid partial matches)
            for (const code of countryCodePatterns.sort((a, b) => b.length - a.length)) {
              if (phoneValue.startsWith(code)) {
                matchedCode = code;
                // Extract phone number part (remove country code and any following space/dash)
                phoneNumber = phoneValue.substring(code.length).trim();
                // Remove spaces, dashes, and other separators
                phoneNumber = phoneNumber.replace(/[\s\-\(\)]/g, '');
                break;
              }
            }
            
            // If no match found, try to extract first 1-4 digits after +
            if (matchedCode === '+1' && phoneValue.length > 1) {
              const match = phoneValue.match(/^\+(\d{1,4})[\s\-]?(.+)$/);
              if (match) {
                matchedCode = '+' + match[1];
                phoneNumber = match[2].replace(/[\s\-\(\)]/g, '');
              }
            }
            
            // Normalize +1 to +1-US (default to US when parsing)
            if (matchedCode === '+1') {
              matchedCode = '+1-US';
            }
            
            setFormData(prev => ({
              ...prev,
              phoneCountryCode: matchedCode,
              phone: phoneNumber.replace(/\D/g, '').slice(0, 15)
            }));
          } else {
            const phoneNumber = phoneValue.replace(/\D/g, '').slice(0, 15);
            setFormData(prev => ({ ...prev, phone: phoneNumber }));
          }
        }
        if (parsedData.headline && !formData.headline) {
          setFormData(prev => ({ ...prev, headline: parsedData.headline || prev.headline }));
        }
        if (parsedData.professionalSummary && !formData.professionalSummary) {
          // Replace newline characters with spaces for a continuous paragraph
          const summary = (parsedData.professionalSummary || '').replace(/\n/g, ' ').replace(/\s+/g, ' ').trim();
          setFormData(prev => ({ ...prev, professionalSummary: summary || prev.professionalSummary }));
        }
        if (parsedData.totalExperienceYears && (!formData.totalExperienceYears || formData.totalExperienceYears === '')) {
          const yearsValue = parsedData.totalExperienceYears;
          if (yearsValue) {
            setFormData(prev => ({ ...prev, totalExperienceYears: String(yearsValue) }));
          }
        }
        if (parsedData.country && !formData.country) {
          setFormData(prev => ({ ...prev, country: parsedData.country || prev.country }));
        }
        if (parsedData.expectedSalary && !formData.expectedSalary) {
          setFormData(prev => ({ ...prev, expectedSalary: parsedData.expectedSalary || prev.expectedSalary }));
        }
        
        // Autofill work experience
        if (parsedData.workExperience && parsedData.workExperience.length > 0 && workExperience.length === 0) {
          const workExp = parsedData.workExperience
            .filter(exp => exp && (exp.company || exp.position || exp.jobTitle)) // Only include entries with at least company or position
            .map((exp, index) => {
              // Replace newlines with spaces in description
              const description = (exp.description || '').replace(/\n/g, ' ').replace(/\s+/g, ' ').trim();
              const position = exp.position || exp.jobTitle || '';
              const startDate = normalizeYearMonth(exp.startDate);
              const rawEndDate = (exp.endDate || '').trim();
              const isCurrent = !rawEndDate || rawEndDate.toLowerCase() === 'present';
              const endDate = isCurrent ? '' : normalizeYearMonth(rawEndDate);
              return {
                id: Date.now().toString() + index,
                company: exp.company || '',
                position,
                startDate,
                endDate,
                description: description,
                isCurrent
              };
            });
          if (workExp.length > 0) {
            setWorkExperience(workExp);
          }
        }
        
        // Autofill education
        if (parsedData.educationEntries && parsedData.educationEntries.length > 0 && educationEntries.length === 0) {
          const edu = parsedData.educationEntries.map((entry, index) => ({
            id: Date.now().toString() + index,
            institution: entry.institution || '',
            degree: entry.degree || '',
            fieldOfStudy: entry.fieldOfStudy || deriveFieldOfStudyFromDegree(entry.degree) || '',
            endYear: extractYear(entry.endYear ?? entry.endDate)
          }));
          setEducationEntries(edu);
        }
        
        // Autofill certifications
        if (parsedData.certificationEntries && parsedData.certificationEntries.length > 0 && certificationEntries.length === 0) {
          const certs = parsedData.certificationEntries.map((cert, index) => ({
            id: Date.now().toString() + index,
            name: cert.name || '',
            issuer: cert.issuer || '',
            issueDate: cert.issueDate || '',
            expiryDate: cert.expiryDate || '',
            credentialId: cert.credentialId || '',
            awardedTo: cert.awardedTo || ''
          }));
          setCertificationEntries(certs);
        }
        
        // Autofill skills
        if (parsedData.skills && parsedData.skills.length > 0 && skills.length === 0) {
          setSkills(parsedData.skills.filter(skill => skill && skill.trim()));
        }
        
        
        
        setResumeParsed(true);
        toast({
          title: "Resume Parsed Successfully!",
          description: "Your resume has been analyzed and the form has been auto-filled. Please review and complete any missing information.",
        });
      }
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        (error instanceof Error ? error.message : null) ||
        "Failed to parse resume. Please try again or fill the form manually.";
      toast({
        title: "Resume Parsing Failed",
        description: message,
        variant: "destructive"
      });
      setResumeFile(null);
    } finally {
      setParsingResume(false);
    }
  };

  const convertCurrency = async (amount: number, from: string, to: string): Promise<number> => {
    if (from === to) return amount;
    
    try {
      // Using exchangerate-api.com free tier (no API key required for basic usage)
      const response = await fetch(`https://api.exchangerate-api.com/v4/latest/${from}`);
      if (!response.ok) {
        throw new Error('Failed to fetch exchange rates');
      }
      const data = await response.json();
      const rate = data.rates[to];
      if (!rate) {
        throw new Error(`Exchange rate not found for ${to}`);
      }
      return amount * rate;
    } catch (error) {
      console.error('Currency conversion error:', error);
      toast({
        title: "Conversion Error",
        description: "Unable to convert currency. Please try again.",
        variant: "destructive"
      });
      return amount; // Return original amount on error
    }
  };

  const handleInputChange = async (field: string, value: string | number) => {
    // Handle currency conversion when currency changes
    if (field === "salaryCurrency" && formData.expectedSalary) {
      const salaryValue = parseFloat(formData.expectedSalary);
      const newCurrency = value as string;
      if (!isNaN(salaryValue) && salaryValue > 0) {
        setConverting(true);
        try {
          const convertedAmount = await convertCurrency(
            salaryValue,
            formData.salaryCurrency,
            newCurrency
          );
          setFormData(prev => ({
            ...prev,
            salaryCurrency: newCurrency,
            expectedSalary: Math.round(convertedAmount).toString()
          }));
          setOriginalSalary(salaryValue);
          setOriginalCurrency(formData.salaryCurrency);
        } catch (error) {
          setFormData(prev => ({ ...prev, salaryCurrency: newCurrency }));
        } finally {
          setConverting(false);
        }
        return;
      }
    }
    
    // Handle other fields
    if (field === "salaryCurrency") {
      setFormData(prev => ({ ...prev, salaryCurrency: value as string }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const addWorkExperience = () => {
    const newExp: WorkExperience = {
      id: Date.now().toString(),
      company: "",
      position: "",
      startDate: "",
      endDate: "",
      description: "",
      isCurrent: false
    };
    setWorkExperience(prev => [...prev, newExp]);
  };

  const updateWorkExperience = (id: string, field: string, value: string | boolean) => {
    setWorkExperience(prev => 
      prev.map(exp => 
        exp.id === id ? { ...exp, [field]: value } : exp
      )
    );
  };

  const removeWorkExperience = (id: string) => {
    setWorkExperience(prev => prev.filter(exp => exp.id !== id));
  };

  const addEducationEntry = () => {
    const newEdu: EducationEntry = {
      id: Date.now().toString(),
      institution: "",
      degree: "",
      fieldOfStudy: "",
      endYear: ""
    };
    setEducationEntries(prev => [...prev, newEdu]);
  };

  const updateEducationEntry = (id: string, field: string, value: string) => {
    setEducationEntries(prev => 
      prev.map(edu => {
        if (edu.id !== id) return edu;
        let updated = { ...edu, [field]: value };

        if (field === "degree") {
          const derived = deriveFieldOfStudyFromDegree(value);
          if (derived && !edu.fieldOfStudy.trim()) {
            updated.fieldOfStudy = derived;
          }
        }

        if (field === "endYear") {
          const digitsOnly = value.replace(/\D/g, '').slice(0, 4);
          updated = { ...updated, [field]: digitsOnly };
        }

        return updated;
      })
    );
  };

  const removeEducationEntry = (id: string) => {
    setEducationEntries(prev => prev.filter(edu => edu.id !== id));
  };

  const addCertification = () => {
    const { name, issuer, issueDate, expiryDate, credentialId, awardedTo } = newCertification;

    if (certificationEntries.length >= MAX_ENTRIES) {
      setCertificationUrlError(`You can add at most ${MAX_ENTRIES} certifications`);
      return;
    }

    if (hasIssues(validateCertificationDraft(newCertification))) {
      setCertDraftAttempted(true);
      setCertificationUrlError("Fix the highlighted certification fields, then add it again");
      return;
    }

    const newCert: CertificationEntry = {
      id: Date.now().toString(),
      name: name.trim(),
      issuer: issuer.trim(),
      issueDate,
      expiryDate,
      credentialId: credentialId.trim() || "",
      awardedTo: awardedTo.trim() || (user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : "")
    };
    setCertificationEntries(prev => [...prev, newCert]);
    setNewCertification({
      name: "",
      issuer: "",
      issueDate: "",
      expiryDate: "",
      credentialId: "",
      awardedTo: ""
    });
    setCertificationUrlError("");
    setCertDraftAttempted(false);
  };

  const removeCertification = (id: string) => {
    setCertificationEntries(prev => prev.filter(c => c.id !== id));
  };

  // Live validation against the API's limits (see components/jobs/application/validation.ts).
  const personalIssues = validatePersonalInfo(formData as ApplicationFormData, user?.email || null);
  const summaryIssues = validateSummary(formData as ApplicationFormData);
  const resumeUrlError = validateResumeUrl(formData.resumeUrl)?.message ?? "";
  const skillsError = validateSkills(skills)?.message ?? "";
  const certDraftIssues = validateCertificationDraft(newCertification);
  // Certifications already in the list (typed or parsed from the resume) can
  // only be removed, not edited, so a sent one that breaks a limit is flagged
  // on its row with the first problem.
  const certEntryErrors: Record<string, string> = {};
  for (const cert of certificationEntries) {
    if (!isSentCertification(cert)) continue;
    const [first] = issueMessages(validateCertificationDraft(cert));
    if (first) certEntryErrors[cert.id] = first;
  }
  const flaggedCertCount = Object.keys(certEntryErrors).length;

  const getStepIssues = (step: number): string[] => {
    const issues: string[] = [];
    switch (step) {
      case 1:
        if (resumeFile === null) issues.push("Upload your resume to continue");
        else if (!resumeParsed) issues.push("Wait for resume parsing to finish");
        break;
      case 2:
        issues.push(...issueMessages(personalIssues));
        break;
      case 3:
        issues.push(...issueMessages(summaryIssues));
        break;
      case 4: {
        if (workExperience.length === 0) {
          issues.push("Add at least one work experience entry");
          break;
        }
        if (workExperience.length > MAX_ENTRIES) {
          issues.push(`List at most ${MAX_ENTRIES} roles (you have ${workExperience.length})`);
        }
        const flagged = workExperience.filter(exp => hasIssues(validateExperienceEntry(exp))).length;
        if (flagged > 0) {
          issues.push(`Fix the highlighted fields in ${flagged} role${flagged > 1 ? "s" : ""}`);
        }
        break;
      }
      case 5: {
        if (educationEntries.length > MAX_ENTRIES) {
          issues.push(`List at most ${MAX_ENTRIES} education entries (you have ${educationEntries.length})`);
        }
        const flagged = educationEntries.filter(edu => hasIssues(validateEducationEntry(edu))).length;
        if (flagged > 0) {
          issues.push(`Fix the highlighted fields in ${flagged} education entr${flagged > 1 ? "ies" : "y"}`);
        }
        break;
      }
      case 6:
        if (resumeFile === null && !(formData.resumeUrl || '').trim()) {
          issues.push("Upload your resume or provide a resume URL");
        }
        if (resumeUrlError) issues.push(resumeUrlError);
        if (skillsError) issues.push(skillsError);
        if (certificationEntries.length > MAX_ENTRIES) {
          issues.push(`List at most ${MAX_ENTRIES} certifications (you have ${certificationEntries.length})`);
        }
        if (flaggedCertCount > 0) {
          issues.push(
            flaggedCertCount > 1
              ? `${flaggedCertCount} certifications have a problem — remove them and add them again with valid details`
              : "A certification has a problem — remove it and add it again with valid details",
          );
        }
        break;
      case 7:
        // Review: everything that is submitted must be valid.
        for (const s of SUBMITTED_STEPS) issues.push(...getStepIssues(s));
        break;
    }
    return issues;
  };

  /** First submitted step that still has a problem, or null. */
  const firstStepWithIssues = (from: number, to: number): { step: number; issues: string[] } | null => {
    for (let step = from; step <= to; step++) {
      if (step === 7) continue;
      const issues = getStepIssues(step);
      if (issues.length > 0) return { step, issues };
    }
    return null;
  };

  /** Send the candidate to a step with problems, revealing every message on it. */
  const showStepIssues = (step: number, issues: string[], title: string) => {
    setRevealedSteps(prev => (prev[step] ? prev : { ...prev, [step]: true }));
    setCurrentStep(step);
    toast({
      title,
      description: issues.join(". "),
      variant: "destructive",
    });
  };

  const nextStep = () => {
    if (currentStep >= steps.length) return;
    const issues = getStepIssues(currentStep);
    if (issues.length > 0) {
      showStepIssues(currentStep, issues, "Please fix this step");
      return;
    }
    setCurrentStep(prev => prev + 1);
  };

  /**
   * Rail navigation: going back is free; going forward passes through the same
   * gate as Continue, so no step with errors can be skipped and discovered only
   * at Submit.
   */
  const jumpToStep = (target: number) => {
    if (target <= currentStep) {
      setCurrentStep(target);
      return;
    }
    const blocked = firstStepWithIssues(currentStep, target - 1);
    if (blocked) {
      showStepIssues(
        blocked.step,
        blocked.issues,
        blocked.step === currentStep ? "Please fix this step" : `Please fix step ${blocked.step} first`,
      );
      return;
    }
    setCurrentStep(target);
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  // One source of truth: a step is complete exactly when it has no issues, so
  // the rail's check marks and the Continue gate can never disagree.
  const isStepValid = (step: number) => getStepIssues(step).length === 0;

  const handleSubmit = async () => {
    if (!job) return;
    
    // Final validation before submission: every field that is sent must pass
    // the same checks the API runs. Land on the first step with a problem, with
    // its fields highlighted, instead of failing on the server.
    const blocked = firstStepWithIssues(SUBMITTED_STEPS[0], SUBMITTED_STEPS[SUBMITTED_STEPS.length - 1]);
    if (blocked) {
      showStepIssues(blocked.step, blocked.issues, `Please fix step ${blocked.step} before submitting`);
      return;
    }

    setLoading(true);

    try {
      // Check authentication first
      if (!isAuthenticated || !user) {
        toast({
          title: "Authentication Required",
          description: "Please log in to submit your application.",
          variant: "destructive"
        });
        navigate(`/auth?redirect=${encodeURIComponent(`/apply/${job.id}`)}`);
        return;
      }

      // Ensure user has a valid ID
      if (!user.id || user.id.trim() === '') {
        toast({
          title: "Authentication Error",
          description: "User ID is missing. Please log out and log back in.",
          variant: "destructive"
        });
        setLoading(false);
        return;
      }

      // Build application data according to the API schema
      const applicationData: any = {};

      // Required fields - firstName and lastName from resume/form, email from account
      // Trimmed, so the length the form validated is the length the API sees.
      applicationData.firstName = formData.firstName.trim();
      applicationData.lastName = formData.lastName.trim();
      applicationData.email = user.email || formData.email;
      
      // Optional fields from form
      if (formData.phone) {
        // Format phone: combine country code with phone number, format as +1-555-9999
        const phoneDigits = formData.phone.replace(/\D/g, '');
        // Format phone number with dashes: +1-555-9999
        let formattedPhoneNumber = '';
        if (phoneDigits.length >= 10) {
          // US/Canada format: area code (3) + exchange (3) + number (4)
          formattedPhoneNumber = `${phoneDigits.slice(0, 3)}-${phoneDigits.slice(3, 6)}-${phoneDigits.slice(6, 10)}`;
        } else if (phoneDigits.length >= 7) {
          // Shorter format: first 3 + rest
          formattedPhoneNumber = `${phoneDigits.slice(0, 3)}-${phoneDigits.slice(3)}`;
        } else {
          // Fallback: just use digits
          formattedPhoneNumber = phoneDigits;
        }
        // Normalize +1-US and +1-CA to +1 for submission
        const normalizedCountryCode = formData.phoneCountryCode.replace(/^\+1-(US|CA)$/, '+1');
        applicationData.phone = `${normalizedCountryCode}-${formattedPhoneNumber}`;
      }
      
      if (formData.headline && formData.headline.trim()) {
        applicationData.headline = formData.headline.trim();
      }
      if (formData.professionalSummary && formData.professionalSummary.trim()) {
        applicationData.professionalSummary = formData.professionalSummary.trim();
      }
      const trimmedResumeUrl = (formData.resumeUrl || '').trim();
      if (trimmedResumeUrl) {
        if (trimmedResumeUrl.startsWith('blob:')) {
          toast({
            title: "Invalid Resume URL",
            description: "The resume URL is a local preview and cannot be submitted. Please provide an external URL or rely on the uploaded file.",
            variant: "destructive"
          });
          setLoading(false);
          return;
        }
        if (/^https?:\/\//i.test(trimmedResumeUrl)) {
          applicationData.resumeUrl = trimmedResumeUrl;
        }
      }
      
      if (formData.totalExperienceYears !== '' && formData.totalExperienceYears !== null && formData.totalExperienceYears !== undefined) {
        const yearsValue = parseInt(String(formData.totalExperienceYears));
        if (!isNaN(yearsValue) && yearsValue > 0) {
          applicationData.totalExperienceYears = yearsValue;
        }
      }
      
      // Work Experience - convert to API format (remove isCurrent, use dates)
      if (workExperience.length > 0) {
        console.log('Processing work experience:', workExperience);
        const processedWorkExp = workExperience
          .map((exp, index) => {
            const company = (exp.company || '').trim();
            const position = (exp.position || '').trim();
            const startDate = (exp.startDate || '').trim() || undefined;
            const endDate = exp.isCurrent ? undefined : ((exp.endDate || '').trim() || undefined);
            const description = (exp.description || '').trim() || undefined;
            
            return {
              company,
              position,
              startDate,
              endDate,
              isCurrent: exp.isCurrent, // Keep this for filtering
              originalIndex: index, // Keep original index for reference
              description
            };
          })
          .filter(exp => {
            // Only include entries that have company, position, and start date
            // End date is required if not current
            const hasCompany = exp.company.length > 0;
            const hasPosition = exp.position.length > 0;
            const hasStartDate = exp.startDate && exp.startDate.length > 0;
            // If isCurrent is true, endDate should be undefined (which is valid)
            // If isCurrent is false, endDate must exist and have length > 0
            const hasValidEndDate = exp.isCurrent ? true : (exp.endDate !== undefined && exp.endDate.length > 0);
            const isValid = hasCompany && hasPosition && hasStartDate && hasValidEndDate;
            if (!isValid) {
              console.warn('Filtering out work experience entry:', exp);
            }
            return isValid;
          })
          .map(exp => {
            const result: any = {
              company: exp.company,
              position: exp.position,
              startDate: normalizeMutationDateValue(exp.startDate)
            };
            const normalizedEnd = normalizeMutationDateValue(exp.endDate);
            if (normalizedEnd) {
              result.endDate = normalizedEnd;
            }
            if (exp.description && exp.description.length > 0) {
              result.description = exp.description;
            }
            return result;
          });
        
        console.log('Processed work experience:', processedWorkExp);
        
        // Only set workExperience if we have valid entries
        if (processedWorkExp.length > 0) {
          applicationData.workExperience = processedWorkExp;
        } else {
          // If API requires at least 1 entry but all were filtered out, show error
          toast({
            title: "Work Experience Required",
            description: "Please ensure at least one work experience entry has both company and position filled in.",
            variant: "destructive"
          });
          setLoading(false);
          return;
        }
      }
      
      // Education - convert to API format
      if (educationEntries.length > 0) {
        // Institution, field of study and end year are required (validated on
        // the Education step). Degree is optional on the API; an empty one is
        // left out because the API rejects an empty string for it.
        const processedEducation = educationEntries
          .map(edu => {
            const degree = (edu.degree || '').trim();
            return {
              institution: (edu.institution || '').trim(),
              ...(degree ? { degree } : {}),
              fieldOfStudy: (edu.fieldOfStudy || '').trim(),
              endYear: (edu.endYear || '').trim() || undefined
            };
          })
          .filter(edu =>
            edu.fieldOfStudy.length > 0 &&
            edu.institution.length > 0 &&
            !!edu.endYear
          );
        
        if (processedEducation.length > 0) {
          applicationData.educationEntries = processedEducation;
        } else if (educationEntries.length > 0) {
          // If we had entries but all were filtered out, show error
          toast({
            title: "Education Validation Failed",
            description: "Please ensure all education entries have institution, field of study, and end year filled in.",
            variant: "destructive"
          });
          setLoading(false);
          return;
        }
      }
      
      // Certifications - convert to API format
      if (certificationEntries.length > 0) {
        const validCertifications = certificationEntries
          .filter(isSentCertification)
          .map(cert => ({
            name: cert.name.trim(),
            issuer: cert.issuer.trim(),
            issueDate: normalizeMutationDateValue(cert.issueDate) ?? cert.issueDate,
            expiryDate: normalizeMutationDateValue(cert.expiryDate) ?? cert.expiryDate,
            credentialId: cert.credentialId?.trim() || undefined,
            awardedTo: cert.awardedTo?.trim() || undefined
          }));
        
        if (validCertifications.length > 0) {
          applicationData.certificationEntries = validCertifications;
        }
      }
      
      if (skills.length > 0) applicationData.skills = skills;
      
      // Format expected salary - can be a single value or range (e.g., "$150,000 - $180,000").
      // Same helper the Contact step validates with, so the length check matches.
      const salaryRaw = (formData.expectedSalary || '').trim();
      if (salaryRaw) {
        applicationData.expectedSalary = buildExpectedSalary(salaryRaw, formData.salaryCurrency);
      }
      
      if (formData.country && formData.country.trim()) {
        applicationData.country = formData.country.trim();
      }

      console.log('Sending application data:', applicationData);
      console.log('User ID:', user.id);
      console.log('User email:', user.email);

      // Get access token from AuthContext (stored during login)
      const accessToken = getAccessToken();
      
      if (!accessToken || accessToken.trim() === '') {
        // If no token, redirect to login
        toast({
          title: "Authentication Required",
          description: "Please log in to submit your application.",
          variant: "destructive"
        });
        navigate(`/auth?redirect=${encodeURIComponent(`/apply/${job.id}`)}`);
        setLoading(false);
        return;
      }

      const result = await jobsApi.apply(job.id, applicationData, accessToken, resumeFile);

      // The application is persisted + associated to the candidate server-side;
      // it surfaces via GET /candidates/me/applications (no localStorage record).
      void result;

      navigate(`/application-submitted/${job.id}`, {
        state: {
          job: {
            title: job.title,
            location: job.location,
            employmentType: job.employmentType,
            workplaceType: job.workplaceType,
            seniorityLevel: job.seniorityLevel,
            salaryRange: job.salaryRange,
          },
          applicant: {
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: user.email || formData.email,
            phone: `${formData.phoneCountryCode.replace(/^\+1-(US|CA)$/, '+1')} ${formData.phone}`,
            resumeFileName: resumeFile?.name,
          },
        },
      });
    } catch (error: any) {
      // BE returns 409 when this candidate already applied — surface it as a
      // benign "already applied" notice and send them back to the posting.
      if (error?.response?.status === 409) {
        toast({
          title: "Already applied",
          description:
            error?.response?.data?.message || "You've already applied to this position.",
        });
        navigate(`/job/${job.id}`);
        return;
      }
      const message =
        error?.response?.data?.message ||
        (error instanceof Error ? error.message : null) ||
        "Something went wrong while submitting your application. Please try again.";
      toast({
        title: "Submission Failed",
        description: message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const getEmploymentTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      'full_time': 'Full Time',
      'Full_time': 'Full Time',
      'full-time': 'Full Time',
      'Full-time': 'Full Time',
      'fulltime': 'Full Time',
      'part_time': 'Part Time',
      'Part_time': 'Part Time',
      'part-time': 'Part Time',
      'Part-time': 'Part Time',
      'parttime': 'Part Time',
      'contract': 'Contract',
      'Contract': 'Contract',
      'internship': 'Internship',
      'Internship': 'Internship',
      'temporary': 'Temporary',
      'Temporary': 'Temporary',
    };
    return labels[type] || type.replace(/[_-]/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  const getWorkplaceTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      'remote': 'Remote',
      'Remote': 'Remote',
      'on_site': 'On Site',
      'On_site': 'On Site',
      'on-site': 'On Site',
      'On-site': 'On Site',
      'onsite': 'On Site',
      'hybrid': 'Hybrid',
      'Hybrid': 'Hybrid',
    };
    return labels[type] || type.replace(/[_-]/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  const getSeniorityLevelLabel = (level: string) => {
    const labels: Record<string, string> = {
      'entry': 'Entry Level',
      'Entry': 'Entry Level',
      'entry_level': 'Entry Level',
      'Entry_level': 'Entry Level',
      'mid': 'Mid Level',
      'Mid': 'Mid Level',
      'mid_level': 'Mid Level',
      'Mid_level': 'Mid Level',
      'senior': 'Senior Level',
      'Senior': 'Senior Level',
      'senior_level': 'Senior Level',
      'Senior_level': 'Senior Level',
      'lead': 'Lead',
      'Lead': 'Lead',
      'director': 'Director',
      'Director': 'Director',
      'executive': 'Executive',
      'Executive': 'Executive',
      'junior': 'Junior',
      'Junior': 'Junior',
    };
    return labels[level] || level.replace(/[_-]/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  if (!job) {
    return (
      <DcPage>
        <ApplicationFormSkeleton />
      </DcPage>
    );
  }

  const currentStepData = steps.find((step) => step.id === currentStep);
  const completedCount = steps.filter((step) => isStepValid(step.id)).length;
  const completionPct = Math.round((completedCount / steps.length) * 100);
  const lockedEmail = user?.email || null;
  const emailValue = user?.email || formData.email;
  const fullName =
    user?.firstName && user?.lastName
      ? `${user.firstName} ${user.lastName}`
      : "Name";

  const autofillSummary = resumeParsed
    ? ([
        (formData.firstName || formData.lastName) && "Contact details",
        formData.professionalSummary && "Summary",
        workExperience.length > 0 &&
          `${workExperience.length} role${workExperience.length > 1 ? "s" : ""}`,
        educationEntries.length > 0 &&
          `${educationEntries.length} education`,
        skills.length > 0 && `${skills.length} skills`,
      ].filter(Boolean) as string[])
    : [];

  const handleSalaryInput = (value: string) => {
    setFormData((prev) => ({ ...prev, expectedSalary: value }));
    setOriginalSalary(null);
    setOriginalCurrency(formData.salaryCurrency);
  };

  const updateCertDraft = (field: string, value: string) => {
    setNewCertification((prev) => ({ ...prev, [field]: value }));
    // The per-field messages take over once the candidate starts fixing it.
    setCertificationUrlError("");
  };

  const formatCertDate = (date: string) => formatDate(date, date, "MMM dd, yyyy");

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <ResumeStep
            file={resumeFile}
            parsing={parsingResume}
            parsed={resumeParsed}
            onSelect={handleResumeUpload}
            onRemove={() => {
              setResumeFile(null);
              setResumeParsed(false);
              setFormData((prev) => ({ ...prev, resumeUrl: "" }));
            }}
            autofilledSummary={autofillSummary}
          />
        );
      case 2:
        return (
          <PersonalInfoStep
            formData={formData as ApplicationFormData}
            onChange={handleInputChange}
            errors={visibleErrors(personalIssues, !!revealedSteps[2])}
            lockedEmail={lockedEmail}
            converting={converting}
            originalSalary={originalSalary}
            originalCurrency={originalCurrency}
            onSalaryChange={handleSalaryInput}
          />
        );
      case 3:
        return (
          <SummaryStep
            formData={formData as ApplicationFormData}
            onChange={handleInputChange}
            errors={visibleErrors(summaryIssues, !!revealedSteps[3])}
          />
        );
      case 4:
        return (
          <ExperienceStep
            items={workExperience}
            onAdd={addWorkExperience}
            onUpdate={updateWorkExperience}
            onRemove={removeWorkExperience}
            showAllErrors={!!revealedSteps[4]}
          />
        );
      case 5:
        return (
          <EducationStep
            items={educationEntries}
            onAdd={addEducationEntry}
            onUpdate={updateEducationEntry}
            onRemove={removeEducationEntry}
            showAllErrors={!!revealedSteps[5]}
          />
        );
      case 6:
        return (
          <SkillsStep
            skills={skills}
            onSkillsChange={setSkills}
            skillsError={skillsError}
            certifications={certificationEntries}
            certDraft={newCertification}
            onCertDraftChange={updateCertDraft}
            onAddCertification={addCertification}
            onRemoveCertification={removeCertification}
            certError={certificationUrlError}
            certErrors={visibleErrors(certDraftIssues, certDraftAttempted)}
            certEntryErrors={certEntryErrors}
            awardedToPlaceholder={fullName}
            formatCertDate={formatCertDate}
            resumeFile={resumeFile}
            resumeParsed={resumeParsed}
            resumeUrl={formData.resumeUrl}
            onResumeUrlChange={(v) => handleInputChange("resumeUrl", v)}
            resumeUrlError={resumeUrlError}
          />
        );
      case 7:
        return (
          <ReviewStep
            formData={formData as ApplicationFormData}
            emailValue={emailValue}
            skills={skills}
            workCount={workExperience.length}
            educationCount={educationEntries.length}
            certCount={certificationEntries.length}
            resumeName={resumeFile?.name}
          />
        );
      default:
        return null;
    }
  };

  return (
    <DcPage>
      <section className="relative py-8 min-h-screen overflow-hidden" style={{ background: "var(--landing-bg)" }}>
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(var(--landing-grid-line) 1px, transparent 1px), linear-gradient(90deg, var(--landing-grid-line) 1px, transparent 1px)",
            backgroundSize: "72px 72px",
            maskImage: "radial-gradient(ellipse 70% 50% at 50% 8%, black, transparent)",
            WebkitMaskImage: "radial-gradient(ellipse 70% 50% at 50% 8%, black, transparent)",
          }}
        />
        <div
          className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[420px] rounded-full pointer-events-none"
          style={{
            background: "radial-gradient(circle, rgba(var(--acc2-rgb),0.12) 0%, rgba(var(--acc2-rgb),0.06) 45%, transparent 70%)",
            filter: "blur(80px)",
            opacity: "var(--landing-glow-mult)",
          }}
        />
        <div className="container relative z-10">
          <div className="mx-auto max-w-5xl">
            <div className="mb-8">
              <button
                type="button"
                onClick={() => navigate(`/job/${jobId}`)}
                className="inline-flex items-center gap-1.5 text-[13px] font-medium transition-colors hover:text-[var(--acc2)] mb-4"
                style={{ color: "var(--landing-text-muted)" }}
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                Back
              </button>
              <h1 className="text-2xl font-bold tracking-tight [overflow-wrap:anywhere]" style={{ color: "var(--landing-text)" }}>
                Apply for{" "}
                <span
                  style={{ color: "var(--acc2)" }}
                >
                  {job.title}
                </span>
              </h1>
            </div>

            <p
              className="mb-6 text-sm"
              style={{ color: "var(--landing-text-muted)" }}
            >
              AI2Aim Technologies · {job.location}
            </p>

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-[300px_minmax(0,1fr)] lg:gap-8">
              {/* ── Control rail: progress + timeline + role context ── */}
              <aside className="space-y-4 lg:sticky lg:top-24 lg:self-start">
                <div className="rounded-2xl p-5" style={glassCardStyle}>
                  <CompletionRing value={completionPct} label="Application" />
                  <div
                    className="my-4 h-px"
                    style={{ background: "var(--landing-glass-border)" }}
                  />
                  <ApplicationStepper
                    steps={steps}
                    current={currentStep}
                    isComplete={isStepValid}
                    onJump={jumpToStep}
                  />
                </div>

                <div className="rounded-2xl p-5" style={glassCardStyle}>
                  <h3
                    className="mb-4 text-[10px] font-bold uppercase tracking-[0.18em]"
                    style={{ color: "var(--landing-text-dim)" }}
                  >
                    Role details
                  </h3>
                  <JobContextPanel
                    location={job.location}
                    employmentType={getEmploymentTypeLabel(job.employmentType)}
                    seniorityLevel={getSeniorityLevelLabel(job.seniorityLevel)}
                    workplaceType={getWorkplaceTypeLabel(job.workplaceType)}
                    salaryRange={job.salaryRange || undefined}
                  />
                </div>
              </aside>

              {/* ── Active step panel ── */}
              <div className="min-w-0">
                <div className="overflow-hidden rounded-2xl" style={glassCardStyle}>
                  <div
                    className="border-b px-4 py-5 sm:px-8"
                    style={{ borderColor: "var(--landing-glass-border)" }}
                  >
                    <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#00B0D8]">
                      Step {currentStep} of {steps.length}
                    </p>
                    <h2
                      className="mt-1 text-xl font-bold tracking-tight"
                      style={{ color: "var(--landing-text)" }}
                    >
                      {currentStepData?.title}
                    </h2>
                    <p className="mt-0.5 text-sm" style={{ color: "var(--landing-text-muted)" }}>
                      {currentStepData?.description}
                    </p>
                  </div>

                  <div className="px-4 py-6 sm:px-8 sm:py-7" style={{ minHeight: 360 }}>
                    <AnimatePresence mode="wait">
                      <motion.div
                        key={currentStep}
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
                      >
                        {renderStep()}
                      </motion.div>
                    </AnimatePresence>
                  </div>

                  <div
                    className="flex flex-wrap items-center justify-between gap-3 border-t px-4 py-4 sm:px-8"
                    style={{
                      borderColor: "var(--landing-glass-border)",
                      background: "var(--landing-surface)",
                    }}
                  >
                    <button
                      type="button"
                      className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors"
                      style={surfaceStyle}
                      onClick={
                        currentStep === 1
                          ? () => navigate(`/job/${jobId}`)
                          : prevStep
                      }
                    >
                      {currentStep === 1 ? (
                        "Cancel"
                      ) : (
                        <>
                          <ArrowLeft className="h-4 w-4" />
                          Back
                        </>
                      )}
                    </button>
                    {currentStep < steps.length ? (
                      <button
                        type="button"
                        className="btn-electric inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-sm font-semibold"
                        onClick={nextStep}
                      >
                        Continue
                        <ChevronRight className="h-4 w-4" />
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="btn-electric inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-sm font-semibold disabled:pointer-events-none disabled:opacity-60"
                        onClick={handleSubmit}
                        disabled={loading}
                      >
                        {loading ? "Submitting…" : "Submit application"}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </DcPage>
  );
}
